import React from "react";
import Emblem from "../../assets/lion.png";
import AadhaarLogo from "../../assets/aadhaar-logo.jpg";
import { groupDigits, formatDob } from "../../utils/fictional";
import { getCardFont } from "../../data/cardFonts";
import "./AadhaarCard.css";

const GENDER_EN = { male: "MALE", female: "FEMALE", other: "OTHER" };

/** CSS custom properties driving the photo's pan/zoom - see the `transform`
    rule on `.ac-photo img` in AadhaarCard.css for the em-based math that
    keeps this correct at every card width without any JS measurement. */
function frameVars(frame) {
  if (!frame) return undefined;
  return { "--fx": frame.x || 0, "--fy": frame.y || 0, "--fs": frame.scale || 1 };
}

/**
 * Corner roundness arrives as a 0-100 dial from Card settings and maps onto
 * the card's em scale, so the curve holds its proportion at every card width -
 * a preview at 380px and an 856px export copy round by the same amount
 * relative to the card, not by the same number of pixels.
 */
export const MAX_CORNER_EM = 3;

export function cardRadiusEm(cornerRadius) {
  const dial = typeof cornerRadius === "number" ? cornerRadius : 35;
  const clamped = Math.min(Math.max(dial, 0), 100);
  return (clamped / 100) * MAX_CORNER_EM;
}

/** The same radius in px for a given card width, for the canvas export paths,
    which have no em to resolve against. Mirrors `font-size: calc(--cw / 50)`
    on .ac-card. */
export function cardRadiusPx(width, cornerRadius) {
  return (width / 50) * cardRadiusEm(cornerRadius);
}

/** --cw drives the whole em-scaled geometry; --card-font is read by .ac-card
    and the Arial-styled elements (see AadhaarCard.css) so a font picked in
    Card settings applies everywhere at once. --card-radius is set here rather
    than left to CSS so the shield and veil that wrap the card can be handed
    the same value (see App.css). */
function cardStyle(width, fontChoice, cornerRadius) {
  return {
    "--cw": `${width}px`,
    "--card-font": getCardFont(fontChoice).stack,
    ...cardRadiusStyle(width, cornerRadius),
  };
}

/**
 * The radius as a custom property for the boxes that wrap a card - the
 * screenshot shield and the blur veil (App.css). Resolved to px here on
 * purpose: those boxes sit outside .ac-card and so inherit the page's 16px
 * font-size, where an em value would come out at a completely different size
 * from the card's own corner.
 */
export function cardRadiusStyle(width, cornerRadius) {
  return { "--card-radius": `${cardRadiusPx(width, cornerRadius)}px` };
}

function SpecimenStrip() {
  return null;
}

/** The identity/verification disclaimer - printed on both faces per this
    project's own choice (a real card only carries it on the back), so
    whichever side someone has visible still carries the disclaimer. */
function NoticeBox() {
  return (
    <div className="ac-notice">
      <p className="ac-script-devanagari">
        आधार पहचान का प्रमाण है, नागरिकता या जन्म तिथि का नहीं। इसका उपयोग
        सत्यापन (ऑनलाइन प्रमाणीकरण, या क्यूआर कोड / ऑफ़लाइन एक्सएमएल की
        स्कैनिंग) के साथ किया जाना चाहिए।
      </p>
      <p>
        <strong>
          Aadhaar is proof of identity, not of citizenship or date of birth.
        </strong>{" "}
        It should be used with verification (online authentication, or
        scanning of QR code / offline XML).
      </p>
    </div>
  );
}

/** The date note printed sideways along the left edge on both the front
    ("Aadhaar no. issued") and back ("Details as on") of the printable
    e-Aadhaar - not a field the PVC card carries, but the paper one does. */
function VertNote({ children, side = "front" }) {
  return (
    <div className={`ac-vert-note ac-vert-note--${side}`}>
      <span>{children}</span>
    </div>
  );
}

function Header({ localTitle, englishTitle, script }) {
  return (
    <header className="ac-head">
      <img className="ac-emblem" src={Emblem} alt="" />
      <div className="ac-head-text">
        <p className={`ac-band ac-band-saffron ac-script-${script}`}>{localTitle}</p>
        <p className="ac-band ac-band-green">{englishTitle}</p>
      </div>
      <img className="ac-logo" src={AadhaarLogo} alt="" />
    </header>
  );
}

function NumberBand({ aadhaar }) {
  return (
    <div className="ac-number-band">
      <p className="ac-number">{groupDigits(aadhaar)}</p>
      <div className="ac-rule" />
    </div>
  );
}

const PHONE_ICON = (
  <path d="M4 5.4c0-1 .8-1.6 1.6-1.6h2c.6 0 1.1.4 1.3 1l1 2.8c.2.5 0 1.1-.4 1.4L8 10.4c1 2.4 2.9 4.3 5.3 5.3l1.4-1.5c.4-.4.9-.5 1.4-.4l2.8 1c.6.2 1 .7 1 1.3v2c0 .8-.6 1.6-1.6 1.6C11 19.7 4.3 13 4 5.4z" />
);
const MAIL_ICON = (
  <>
    <rect x="3.4" y="5.6" width="17.2" height="12.8" rx="1.6" />
    <path d="m4 6.6 8 6.4 8-6.4" />
  </>
);
const GLOBE_ICON = (
  <>
    <circle cx="12" cy="12" r="8.4" />
    <path d="M3.6 12h16.8M12 3.6c2.4 2.3 3.7 5.2 3.7 8.4s-1.3 6.1-3.7 8.4c-2.4-2.3-3.7-5.2-3.7-8.4S9.6 5.9 12 3.6z" />
  </>
);

/** UIDAI's helpline row - only the back of the printable e-Aadhaar carries
    it; the PVC card has no room for it. */
function ContactFooter() {
  return (
    <ul className="ac-contact">
      <li>
        <svg viewBox="0 0 24 24" aria-hidden="true">{PHONE_ICON}</svg>
        1947
      </li>
      <li>
        <svg viewBox="0 0 24 24" aria-hidden="true">{MAIL_ICON}</svg>
        help@uidai.gov.in
      </li>
      <li>
        <svg viewBox="0 0 24 24" aria-hidden="true">{GLOBE_ICON}</svg>
        www.uidai.gov.in
      </li>
    </ul>
  );
}

export const CardFront = React.forwardRef(function CardFront(
  { data, lang, width = 500 },
  ref
) {
  return (
    <div className="ac-card" ref={ref} style={cardStyle(width, data.fontChoice, data.cornerRadius)}>
      {data.issuedDate && <VertNote side="front">Aadhaar no. issued: {formatDob(data.issuedDate)}</VertNote>}

      <Header
        localTitle={lang.govt}
        englishTitle="Government of India"
        script={lang.script}
      />

      <div className="ac-body ac-body-front">
        <div className="ac-front-left">
          <div className="ac-photo">
            {data.photo ? (
              <img src={data.photo} alt="Portrait" style={frameVars(data.photoFrame)} />
            ) : (
              <div className="ac-photo-empty">
                <svg viewBox="0 0 64 80" aria-hidden="true">
                  <circle cx="32" cy="24" r="15" />
                  <path d="M4 80c0-16 12.5-27 28-27s28 11 28 27z" />
                </svg>
              </div>
            )}
          </div>

          <div className="ac-fields">
            {data.nameLocal && (
              <p className={`ac-name-local ac-script-${lang.script}`}>{data.nameLocal}</p>
            )}
            {data.name && <p className="ac-name">{data.name}</p>}
            <p className="ac-line">
              <span className={`ac-script-${lang.script}`}>{lang.dob}</span>
              <span className="ac-sep"> / </span>DOB : {formatDob(data.dob)}
            </p>
            <p className="ac-line">
              <span className={`ac-script-${lang.script}`}>{lang.gender[data.gender]}</span>
              <span className="ac-sep"> / </span>
              {GENDER_EN[data.gender]}
            </p>

            <NoticeBox />
          </div>
        </div>
      </div>

      <NumberBand aadhaar={data.aadhaar} />

      <p className="ac-slogan ac-script-devanagari">
        मेरा <span>आधार</span>, मेरी पहचान
      </p>

      <SpecimenStrip />
    </div>
  );
});

export const CardBack = React.forwardRef(function CardBack(
  { data, lang, qr, width = 500 },
  ref
) {
  const detailsAsOn = formatDob(new Date().toISOString().slice(0, 10));

  return (
    <div className="ac-card" ref={ref} style={cardStyle(width, data.fontChoice, data.cornerRadius)}>
      <VertNote side="back">Details as on: {detailsAsOn}</VertNote>

      <Header
        localTitle={lang.uidai}
        englishTitle="Unique Identification Authority of India"
        script={lang.script}
      />

      <div className="ac-body ac-body-back">
        <div className="ac-back-left">
          <div className="ac-address">
            {data.addressLocal && (
              <p className={`ac-addr ac-script-${lang.script}`}>
                <span className="ac-addr-label">{lang.address}</span>
                {data.addressLocal}
              </p>
            )}
            {data.address && (
              <p className="ac-addr">
                <span className="ac-addr-label">Address</span>
                {data.address}
              </p>
            )}
          </div>

          <NoticeBox />
        </div>

        <div className="ac-back-right">
          <div className="ac-qr">
            {qr ? <img src={qr} alt="QR code" /> : <div className="ac-qr-empty" />}
          </div>
        </div>
      </div>

      <ContactFooter />

      <NumberBand aadhaar={data.aadhaar} />
      <p className="ac-vid ac-vid-center">VID : {groupDigits(data.vid)}</p>

      <p className="ac-slogan ac-script-devanagari">
        मेरा <span>आधार</span>, मेरी पहचान
      </p>

      <SpecimenStrip />
    </div>
  );
});
